import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    LOCALE_ID,
    provideAppInitializer,
    provideZoneChangeDetection
} from '@angular/core';
import { provideRouter, RouteReuseStrategy } from '@angular/router';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { provideEffects } from '@ngrx/effects';
import { provideStore, Store } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
    BrowserOnlyServerService,
    ConfigurationService,
    CustomErrorOverridesService,
    ErrorHandlerService,
    GeneralErrorService,
    ImageFormatSupportService,
    LOCAL_CONFIG,
    LOCAL_CONFIG_OVERRIDES,
    NavigationService,
    PhxRouteReuseStrategy,
    ServerService,
    ServerSideRelativeUrlHttpInterceptor,
    SimpleGeneralErrorService
} from '@phx/core';
import { configurationAppInitializerFn } from '@phx/core/configuration/configuration-app-initializer-factory';
import { CustomStabilizationHttpInterceptor } from '@phx/core/service/custom-stabilization.interceptor.service';
import { localConfigOverrides } from '../configs/local-configuration-overrides';
import * as localConfiguration from '../configs/local-configuration.json';
import { routes } from './app.routes';
import { autologinAppInitializerFn } from './auth/autologin-initializer-factory';
import { FullAuthNeededHttpInterceptorService } from './auth/services/full-auth-needed-http-interceptor.service';
import { AuthEffects } from './auth/store/auth.effects';
import { authFeature } from './auth/store/auth.state';
import { MatomoTagManagerService } from './services/matomo-tag-manager.service';
import { MerchantCockpitCustomErrorOverridesService } from './services/merchant-cockpit-custom-error-overrides.service';
import { NgbGermanDateAdapter, NgbGermanDateParserFormatter } from './utils/date-utils';

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection(),
        provideRouter(routes),
        provideStore({ auth: authFeature.reducer }),
        provideEffects(AuthEffects),
        provideStoreDevtools({
            name: 'Merchant Cockpit',
            maxAge: 25, // Retains last 25 states
            logOnly: true, // Restrict extension to log-only mode
            autoPause: true,
            trace: false,
            connectInZone: true
        }),
        provideAppInitializer(() => {
            const initializerFn = configurationAppInitializerFn(inject(ConfigurationService));
            return initializerFn();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ServerSideRelativeUrlHttpInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CustomStabilizationHttpInterceptor,
            multi: true
        },
        {
            provide: LOCAL_CONFIG_OVERRIDES,
            useValue: localConfigOverrides
        },
        {
            provide: LOCAL_CONFIG,
            useValue: localConfiguration['default']
        },
        provideAppInitializer(() => {
            const initializerFn = ((navService: NavigationService) => {
                return function initNavService() {
                    navService.init();
                };
            })(inject(NavigationService));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = ((imageFormatSupportService: ImageFormatSupportService) => {
                return function initImageFormatSupport() {
                    imageFormatSupportService.init();
                };
            })(inject(ImageFormatSupportService));
            return initializerFn();
        }),
        { provide: LOCALE_ID, useValue: 'de' },
        {
            provide: ErrorHandler,
            useClass: ErrorHandlerService
        },
        {
            provide: RouteReuseStrategy,
            useClass: PhxRouteReuseStrategy
        },
        {
            provide: ServerService,
            useClass: BrowserOnlyServerService
        },
        {
            provide: CustomErrorOverridesService,
            useClass: MerchantCockpitCustomErrorOverridesService
        },
        {
            provide: GeneralErrorService,
            useClass: SimpleGeneralErrorService
        },
        provideAppInitializer(() => {
            const initializerFn = autologinAppInitializerFn(inject(Store));
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = ((
                matomoTagManager: MatomoTagManagerService,
                configurationService: ConfigurationService
            ) => {
                return function initTracking() {
                    if (configurationService.get<boolean>('tracking.matomo.mtm.enable')) {
                        const mtmId = configurationService.get<string>('tracking.matomo.mtm.id');
                        const mtmUrlTemplate = configurationService.get<string>('tracking.matomo.mtm.url');
                        matomoTagManager.initQueue(mtmId, mtmUrlTemplate);
                        matomoTagManager.init();
                    }
                };
            })(inject(MatomoTagManagerService), inject(ConfigurationService));
            return initializerFn();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FullAuthNeededHttpInterceptorService,
            multi: true
        },
        { provide: NgbDateAdapter, useClass: NgbGermanDateAdapter },
        { provide: NgbDateParserFormatter, useClass: NgbGermanDateParserFormatter },
        provideHttpClient(withInterceptorsFromDi())
    ]
};
